// RightRail — die rechte Sidebar mit 4 Tabs (Daten / News / Whales / Mein).
// Crowd (Polymarket + Trending) wurde nach Doc-Wunsch in die linke Sidebar
// als Sub-Item unter „Markt" ausgelagert.
// Source-of-truth: mockups/03-shell/B-bolt-hero.html (rechte Spalte).

import { useState } from 'react';
import { useLocation } from 'react-router-dom';
import { Anchor, BarChart3, Newspaper, User } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { TabDaten } from '@/components/right-rail/tab-daten';
import { TabNews } from '@/components/right-rail/tab-news';
import { TabWhales } from '@/components/right-rail/tab-whales';
import { TabMein } from '@/components/right-rail/tab-mein';
import { selectFullData, useMarketStore } from '@/stores/market-store';
import { selectWhaleAlerts, useWhaleAlertsStore } from '@/stores/whale-alerts-store';

type TabKey = 'daten' | 'news' | 'whales' | 'mein';

interface TabDef {
  key: TabKey;
  label: string;
  icon: LucideIcon;
  /** Optional Count-Badge (z.B. NEW news count, alert count). */
  count?: number;
  countColor?: 'amber' | 'rose';
}

export function RightRail() {
  const location = useLocation();
  const [active, setActive] = useState<TabKey>('daten');
  const whaleCount = useWhaleAlertsStore(selectWhaleAlerts).length;
  const fullData = useMarketStore(selectFullData);

  // /community hat eigene Member-Rail rechts — die Daten-RightRail würde
  // den Inhalt zerquetschen und die Konkurrenz um den Right-Slot auslösen.
  if (location.pathname === '/community') return null;
  const newsCount =
    (fullData?.newsApi?.articles?.length ?? 0) +
    (fullData?.newsApiGeo?.articles?.length ?? 0);

  const tabs: ReadonlyArray<TabDef> = [
    { key: 'daten', label: 'Daten', icon: BarChart3 },
    { key: 'news', label: 'News', icon: Newspaper, count: newsCount, countColor: 'amber' },
    { key: 'whales', label: 'Whales', icon: Anchor, count: whaleCount, countColor: 'rose' },
    { key: 'mein', label: 'Mein', icon: User },
  ];

  return (
    <aside className="glass-bar flex w-80 shrink-0 flex-col border-l border-white/5">
      {/* Tab header */}
      <div className="flex h-10 shrink-0 items-stretch gap-0.5 border-b border-white/5 px-1.5">
        {tabs.map((t) => {
          const Icon = t.icon;
          const isActive = active === t.key;
          const labelCountColor =
            t.countColor === 'rose' ? 'text-rose-300' : 'text-neutral-500';
          return (
            <button
              key={t.key}
              type="button"
              onClick={() => setActive(t.key)}
              className={`my-1 flex flex-1 items-center justify-center gap-1 rounded-md border text-[11px] transition ${
                isActive
                  ? 'border-amber-500/30 bg-amber-500/15 text-amber-300'
                  : 'border-transparent text-neutral-400 hover:bg-white/5 hover:text-white'
              }`}
            >
              <Icon className="size-3" />
              {t.label}
              {t.count != null && t.count > 0 ? (
                <span className={`text-[10px] ${isActive ? 'text-amber-300/80' : labelCountColor}`}>
                  {t.count}
                </span>
              ) : null}
            </button>
          );
        })}
      </div>

      {/* Tab body */}
      <div className="scroll-hidden flex-1 space-y-3 overflow-y-auto p-3">
        {active === 'daten' ? <TabDaten /> : null}
        {active === 'news' ? <TabNews /> : null}
        {active === 'whales' ? <TabWhales /> : null}
        {active === 'mein' ? <TabMein /> : null}
      </div>
    </aside>
  );
}
